// src\components\planning\OrdersList.jsx
import React, { useState, useRef } from 'react';
import { AlertTriangle, X, ArrowUp, ArrowDown, Filter, ChevronRight } from 'lucide-react';

const OrdersList = ({ 
  orders, 
  selectedBatch, 
  onOrderDrop, 
  resources,
  batches,
  onTouchStart,
  onTouchMove,
  onTouchEnd
}) => {
  const [draggingOrderId, setDraggingOrderId] = useState(null);
  const [filterCriteria, setFilterCriteria] = useState('deadline');
  const [showFilters, setShowFilters] = useState(false);
  const [touchStartX, setTouchStartX] = useState(null);
  
  const dropZoneRef = useRef(null);
  const orderItemsRef = useRef({});

  // Fonction pour obtenir la couleur en fonction de la priorité
  const getPriorityColor = (priority) => {
    switch(priority) {
      case 'high': return 'bg-red-100 text-red-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  // Fonction pour obtenir la couleur en fonction du cycle
  const getCycleColor = (cycleName) => {
    switch(cycleName) {
      case 'A42': return 'border-blue-500';
      case 'A35': return 'border-purple-500';
      case 'E25': return 'border-green-500';
      case 'A29': return 'border-red-500';
      case 'E28': return 'border-yellow-500';
      default: return 'border-gray-500';
    }
  };

  // Filtrer les ordres non affectés pour montrer seulement ceux qui correspondent au cycle sélectionné
  const filterUnassignedOrders = () => {
    const unassignedOrders = orders.filter(order => !order.assignedTo);
    
    if (selectedBatch) {
      return unassignedOrders.filter(order => order.cycleName === selectedBatch.cycleName);
    }
    
    return unassignedOrders;
  };

  // Trier les ordres en fonction du critère de tri
  const sortOrders = (orders) => {
    return [...orders].sort((a, b) => {
      switch(filterCriteria) {
        case 'deadline':
          // Convertir les dates DD/MM/YYYY en objets Date
          const [dayA, monthA, yearA] = a.deadline.split('/').map(Number);
          const [dayB, monthB, yearB] = b.deadline.split('/').map(Number);
          const dateA = new Date(yearA, monthA - 1, dayA);
          const dateB = new Date(yearB, monthB - 1, dayB);
          return dateA - dateB;
        case 'priority':
          const priorityValue = { high: 3, medium: 2, low: 1 };
          return priorityValue[b.priority] - priorityValue[a.priority];
        case 'cycle':
          return a.cycleName.localeCompare(b.cycleName);
        default:
          return 0;
      }
    });
  };

  // Appliquer le filtrage et le tri
  const filteredUnassignedOrders = sortOrders(filterUnassignedOrders());

  // Vérifier si un OF est en retard par rapport à sa date de livraison
  const isOrderLate = (order) => {
    // Convertir les dates de format "DD/MM/YYYY" à des objets Date
    const [orderDay, orderMonth, orderYear] = order.deadline.split('/').map(Number);
    const currentDate = new Date();
    
    const orderDate = new Date(orderYear, orderMonth - 1, orderDay);
    
    return currentDate > orderDate;
  };

  // Gestionnaires pour le glisser-déposer
  const handleDragStart = (e, order) => {
    setDraggingOrderId(order.id);
    e.dataTransfer.setData('text/plain', order.id);
    e.dataTransfer.effectAllowed = 'move';
    
    // Ajouter la classe pour la visualisation du glissement
    if (e.currentTarget.classList) {
      e.currentTarget.classList.add('dragging');
    }
  };

  const handleDragEnd = (e) => {
    setDraggingOrderId(null);
    // Supprimer la classe une fois le glissement terminé
    if (e.currentTarget.classList) {
      e.currentTarget.classList.remove('dragging');
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    if (e.currentTarget.classList) {
      e.currentTarget.classList.add('bg-blue-50');
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    if (e.currentTarget.classList) {
      e.currentTarget.classList.remove('bg-blue-50');
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    if (e.currentTarget.classList) {
      e.currentTarget.classList.remove('bg-blue-50');
    }
    
    if (!selectedBatch) return;
    
    const orderId = e.dataTransfer.getData('text/plain');
    if (!orderId) return;
    
    onOrderDrop(orderId, selectedBatch.batchNumber);
    setDraggingOrderId(null);
  };

  const handleRemoveFromBatch = (e, orderId) => {
    e.stopPropagation();
    onOrderDrop(orderId, null);
  };

  // Gestionnaires d'événements pour les appareils tactiles
  const handleOrderTouchStart = (e, order) => {
    // Mémoriser la position initiale horizontale du toucher
    setTouchStartX(e.touches[0].clientX);
    
    // Appeler le gestionnaire parent pour le touch start
    if (onTouchStart) {
      onTouchStart(e, order.id);
    }
  };

  const handleOrderTouchMove = (e) => {
    // Calculer le déplacement horizontal
    if (touchStartX !== null && e.touches.length > 0) {
      const currentX = e.touches[0].clientX;
      const deltaX = touchStartX - currentX;
      
      // Si le déplacement est significatif vers la gauche (swipe vers le détail)
      if (deltaX > 50) {
        // Ajouter une classe visuelle pour indiquer qu'on s'approche de la zone de dépôt
        e.currentTarget.classList.add('swiping-left');
      } else {
        e.currentTarget.classList.remove('swiping-left');
      }
    }
    
    // Appeler le gestionnaire parent pour le touch move
    if (onTouchMove) {
      onTouchMove(e);
    }
  };

  const handleOrderTouchEnd = (e) => {
    // Réinitialiser la position de départ
    setTouchStartX(null);
    
    // Supprimer la classe visuelle
    e.currentTarget.classList.remove('swiping-left');
    
    // Appeler le gestionnaire parent pour le touch end
    if (onTouchEnd) {
      onTouchEnd(e);
    }
  };

  // Obtenir les ordres assignés à la fournée sélectionnée
  const getOrdersForBatch = (batchNumber) => {
    return orders.filter(order => order.assignedTo === batchNumber);
  };

  return (
    <div className="flex flex-col flex-grow overflow-hidden">
      {/* En-tête avec les filtres */}
      <div className="p-2 bg-gray-50 border-b flex justify-between items-center">
        <div className="text-sm font-medium">Ordres non assignés ({filteredUnassignedOrders.length})</div>
        <button 
          className="flex items-center text-xs text-gray-600 hover:text-gray-800 bg-gray-200 hover:bg-gray-300 rounded px-2 py-1"
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter size={14} className="mr-1" />
          Filtres
        </button>
      </div>
      
      {/* Filtres déroulants */}
      {showFilters && (
        <div className="p-2 bg-gray-50 border-b">
          <div className="text-sm font-medium mb-1">Trier par:</div>
          <div className="flex space-x-2 mb-2">
            <button 
              className={`px-2 py-1 text-xs rounded ${filterCriteria === 'deadline' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
              onClick={() => setFilterCriteria('deadline')}
            >
              Échéance
            </button>
            <button 
              className={`px-2 py-1 text-xs rounded ${filterCriteria === 'priority' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
              onClick={() => setFilterCriteria('priority')}
            >
              Priorité
            </button>
            <button 
              className={`px-2 py-1 text-xs rounded ${filterCriteria === 'cycle' ? 'bg-blue-600 text-white' : 'bg-gray-200'}`}
              onClick={() => setFilterCriteria('cycle')}
            >
              Cycle
            </button>
          </div>
        </div>
      )}
      
      {/* Liste des OF non assignés */}
      <div className="flex-grow overflow-hidden">
        <div className="h-full max-h-[80vh] overflow-y-auto">
          <div className="p-2 space-y-2">
            {filteredUnassignedOrders.map(order => (
              <div 
                key={order.id}
                ref={el => orderItemsRef.current[order.id] = el}
                className={`p-2 border ${getCycleColor(order.cycleName)} border-l-4 rounded flex justify-between items-start cursor-move hover:shadow-sm relative transition-all`}
                draggable
                onDragStart={(e) => handleDragStart(e, order)}
                onDragEnd={handleDragEnd}
                onTouchStart={(e) => handleOrderTouchStart(e, order)}
                onTouchMove={handleOrderTouchMove}
                onTouchEnd={handleOrderTouchEnd}
              >
                {/* Première colonne */}
                <div>
                  <div className="flex items-center justify-between">
                    <div className="font-medium">{order.id}</div>
                  </div>
                  <div className="flex items-center space-x-4 mt-1 text-xs">
                    <div className="font-medium text-sm">{order.cycleName}</div>
                      {selectedBatch && (
                        <button 
                          className="mt-2 flex items-center text-xs px-2 py-1 bg-blue-100 text-blue-700 rounded-lg hover:bg-blue-200"
                          onClick={() => onOrderDrop(order.id, selectedBatch.batchNumber)}
                        >
                          <ChevronRight size={14} className="mr-1" />
                          Assigner
                        </button>
                      )}

                  </div>
                </div>
                {/* Deuxième colonne */}
                <div>
                  <div className="flex items-center justify-between">
                    <div className="font-medium text-sm text-gray-600">{order.name}</div>
                  </div>
                  <div className="flex items-center space-x-4 mt-1 text-xs">
                    <div className="flex items-center">
                      <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" className="mr-1 text-purple-600">
                        <circle cx="12" cy="12" r="10" fill="none" strokeWidth="2"></circle>
                        <path d="M8 12h8" strokeWidth="2"></path>
                        <path d="M12 8v8" strokeWidth="2"></path>
                      </svg>
                      <span>{order.vacuumPorts}</span>
                    </div>
                    <div className="flex items-center">
                      <svg viewBox="0 0 24 24" width="14" height="14" stroke="currentColor" className="mr-1 text-red-600">
                        <path d="M12 2v6M12 22v-6M4.93 4.93l4.24 4.24M14.83 14.83l4.24 4.24M2 12h6M22 12h-6M4.93 19.07l4.24-4.24M14.83 9.17l4.24-4.24" fill="none" strokeWidth="2"></path>
                      </svg>
                      <span>{order.tc}</span>
                    </div>
                  </div>
                </div>
                {/* Troisième colonne */}
                <div className="flex flex-col items-end">
                  <div className="flex items-center space-x-4 mt-1 text-xs">
                    {isOrderLate(order) && (
                        <div className="ml-2 flex items-center text-red-600 text-xs">
                          <AlertTriangle size={14} className="mr-1" />
                          En retard
                        </div>
                      )}
                    <div className="text-gray-500 text-sm">Échéance: {order.deadline}</div>
                  </div>
                  <div className={`mt-1 text-xs ${getPriorityColor(order.priority)} px-2 py-1 rounded-full`}>
                      {order.priority === 'high' ? 'Haute' : 
                      order.priority === 'medium' ? 'Moyenne' : 'Basse'}
                  </div>
                </div>

                {/* Indicateur de swipe (visible uniquement sur les appareils tactiles) */}
                <div className="swipe-indicator">
                  <ChevronRight size={24} className="text-blue-600" />
                  <span className="text-xs">Glisser pour assigner</span>
                </div>
              </div>
            ))}
            
            {filteredUnassignedOrders.length === 0 && (
              <div className="text-center py-8 text-gray-400">
                Aucun OF non assigné pour ce cycle
              </div>
            )}
          </div>
        </div>
      </div>
      
      {/* Zone de dépôt pour la fournée sélectionnée */}
      {selectedBatch && (
        <div 
          className="p-3 border-t bg-blue-50"
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
          onDrop={handleDrop}
          ref={dropZoneRef}
        >
          <div className="text-center text-blue-700 font-medium text-sm py-2">
            Glissez-déposez ici pour ajouter à la fournée {selectedBatch.batchNumber}
          </div>
        </div>
      )}
      
      <style jsx="true">{`
        .dragging {
          opacity: 0.6;
          transform: scale(0.95);
          box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
        }
        
        .swiping-left {
          transform: translateX(-20px);
        }
        
        .swipe-indicator {
          position: absolute;
          right: -30px;
          top: 50%;
          transform: translateY(-50%);
          opacity: 0;
          transition: opacity 0.2s ease-in-out;
          display: flex;
          flex-direction: column;
          align-items: center;
        }
        
        .swiping-left .swipe-indicator {
          opacity: 1;
        }
        
        /* Support uniquement le défilement vertical, pas le défilement horizontal */
        .overflow-y-auto {
          overscroll-behavior-y: contain;
          -webkit-overflow-scrolling: touch;
        }
        
        /* Styles spécifiques aux appareils tactiles */
        @media (hover: none) and (pointer: coarse) {
          /* Styles pour les écrans tactiles */
          .cursor-move {
            cursor: default;
          }
        }
      `}</style>
    </div>
  );
};

export default OrdersList;